import React, { useState } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Space, Typography } from 'antd';
import { LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { getMenuIcon } from './menuIcons';
import { useI18n } from '../hooks/useI18n';
import PermissionGate from './PermissionGate';
// import { useSettings } from '../contexts/SettingsContext';
import TenantManager from './TenantManager';
import TenantManagement from './TenantManagement';
// import PermissionManager from './PermissionManager'; // 已替换为新的组件
import RolePermissionsManager from './RolePermissionsManager';
import PermissionsManager from './PermissionsManager';
import UserPermissionsManager from './UserPermissionsManager';

import JobManager from './JobManager';
import InstructionManager from './InstructionManager';
import InstructionSyncManager from './InstructionSyncManager';
import SqlPairManager from './SqlPairManager';
import SyncManager from './SyncManager';
import ModelingManager from './ModelingManager';
import TemplateManager from './TemplateManager';
import MCPManager from './MCPManager';
import ServiceManager from './ServiceManager';
import AgentsManagement from './AgentsManagement';
import TenantSelector from './TenantSelector';
import WhatsAppAccountsPage from './WhatsAppAccountsPage';
import AccountMappingsPage from './AccountMappingsPage';
import ConnectorMonitoringPage from './ConnectorMonitoringPage';
import UserSettings from './UserSettings';
import InternationalizationTest from './InternationalizationTest';
// import TranslationTest from './TranslationTest';
import WhatsAppConnectorTestPage from './WhatsAppConnectorTestPage';
import WhatsAppConnectorMonitorPage from './WhatsAppConnectorMonitorPage';
import WhatsAppPartnersPage from './WhatsAppPartnersPage';
import IBuildProxyDataViewer from './IBuildProxyDataViewer';
import IBuildProxyMQManagement from './IBuildProxyMQManagement';
import IBuildProxyCleanupManagement from './IBuildProxyCleanupManagement';
import IBuildProxyAgentManagement from './IBuildProxyAgentManagement';
import AgentConversationsPage from './AgentConversationsPage';
import AgentMessagesPage from './AgentMessagesPage';
import RawMessagesPage from './RawMessagesPage';
import UserProfilePage from './UserProfilePage';
import AgentWrokflowManager from './AgentWrokflowManager';
import EmployeeManagementPage from './EmployeeManagementPage';
import CompanyManagementPage from './CompanyManagementPage';
import TestAgentManager from './TestAgentManager';
import ErrorBoundary from './ErrorBoundary';
import { useAuth } from '../contexts/AuthContext';
import PermissionSwitchConfig from './PermissionSwitchConfig';
import DeploymentManagement from './DeploymentManagement';
import HealthCheckLogs from './HealthCheckLogs';
import EmailConfigManager from './EmailConfigManager';

const { Header, Content, Sider } = Layout;
const { Title } = Typography;

const MainLayout: React.FC = () => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, isSuperAdmin, logout } = useAuth();
  const [selectedKey, setSelectedKey] = useState(isSuperAdmin ? 'tenant-management' : 'instructions');
  const [refreshKey, setRefreshKey] = useState(0);
  
  const { t } = useI18n();
  // const { settings } = useSettings();

  // 构建租户管理菜单项（只有超级管理员可见）
  // 方法1: 使用条件渲染来控制菜单项的显示
  // 方法2: 使用 PermissionGate 组件来包装整个菜单项
  // 
  // 示例：使用 PermissionGate 控制菜单项
  // <PermissionGate requireSuperAdmin={true}>
  //   <Menu.Item key="admin-only-menu" icon={<SettingOutlined />}> 
  //     {t('menu.adminOnly')}
  //   </Menu.Item>
  // </PermissionGate>
  // Tenant Management 子菜单：仅超级管理员可见（与 User 相同策略）
  const tenantChildren = isSuperAdmin
    ? [
        { key: 'tenant-management', icon: getMenuIcon('tenant-management'), label: t('menu.tenants') },
        { key: 'user', icon: getMenuIcon('user'), label: t('menu.user') },
        { key: 'sync-manager', icon: getMenuIcon('sync-manager'), label: t('menu.syncManager') },
      ]
    : [];

  // 根据用户权限构建菜单项
  const menuItems = isSuperAdmin ? [
    // 超级管理员可以看到所有菜单
    // 租户管理菜单（只有超级管理员可见）
    ...(tenantChildren.length > 0 ? [{
      key: 'tenant',
      icon: getMenuIcon('tenant'),
      label: t('menu.tenants'),
      children: tenantChildren,
    }] : []),
    {
      key: 'service-management',
      icon: getMenuIcon('service-management'),
      label: t('menu.services'),
      children: [
        { key: 'server', icon: getMenuIcon('server'), label: t('menu.services') },
        { key: 'health-check-logs', icon: getMenuIcon('health-check-logs'), label: t('menu.healthCheckLogs') },
        { key: 'deployment-management', icon: getMenuIcon('deployment-management'), label: t('menu.deploymentManagement') },
      ],
    },
    {
      key: 'job',
      icon: getMenuIcon('job'),
      label: t('menu.jobs'),
      children: [
        { key: 'instruction-sync', icon: getMenuIcon('instruction-sync'), label: t('menu.instructions') },
        { key: 'modeling-sync', icon: getMenuIcon('modeling-sync'), label: t('menu.modeling') },
        { key: 'sql-pair-sync', icon: getMenuIcon('sql-pair-sync'), label: t('sqlPair.title') },
      ],
    },
    {
      key: 'role-permissions',
      icon: getMenuIcon('role-permissions'),
      label: t('menu.rolePermissions'),
      children: [
        { key: 'role-permissions-manager', icon: getMenuIcon('role-permissions-manager'), label: t('menu.rolePermissions') },
        { key: 'user-permissions-manager', icon: getMenuIcon('user-permissions-manager'), label: t('menu.userPermissions') },
        { key: 'permissions-manager', icon: getMenuIcon('permissions-manager'), label: t('menu.permissions') },
        { key: 'permission-switch-config', icon: getMenuIcon('permission-switch-config'), label: t('menu.permissionSwitchConfig') },
      ],
    },
    { key: 'instructions', icon: getMenuIcon('instructions'), label: t('menu.instructions') },
    { key: 'sql-pairs', icon: getMenuIcon('sql-pairs'), label: t('sqlPair.title') },
    { key: 'modeling', icon: getMenuIcon('modeling'), label: t('menu.modeling') },
    { key: 'template', icon: getMenuIcon('template'), label: t('menu.templates') },
    { key: 'mcp', icon: getMenuIcon('mcp'), label: t('menu.mcp') },
    {
      key: 'connector',
      icon: getMenuIcon('connector'),
      label: t('menu.connector'),
      children: [
        {
          key: 'connector-group',
          label: t('menu.connectorGroup'),
          icon: getMenuIcon('connector-group'),
          children: [
            { key: 'connector-monitor', label: t('menu.connectorMonitor'), icon: getMenuIcon('connector-monitor') },
          ],
        },
        {
          key: 'whatsapp-group',
          label: t('menu.whatsappGroup'),
          icon: getMenuIcon('whatsapp-group'),
          children: [
            { key: 'whatsapp-partners', label: t('menu.whatsappPartners'), icon: getMenuIcon('whatsapp-partners') },
            { key: 'whatsapp-accounts', label: t('menu.whatsappAccounts'), icon: getMenuIcon('whatsapp-accounts') },
            { key: 'account-mappings', label: t('menu.accountMappings'), icon: getMenuIcon('account-mappings') },
            { key: 'whatsapp-messages', label: t('menu.whatsappMessages'), icon: getMenuIcon('whatsapp-messages') },
            { key: 'whatsapp-test', label: t('menu.whatsappTest'), icon: getMenuIcon('whatsapp-test') },
          ],
        },
        {
          key: 'agent-group',
          label: t('menu.agentGroup'),
          icon: getMenuIcon('agent-group'),
          children: [
            { key: 'agents', label: t('menu.agents'), icon: getMenuIcon('agents') },
            { key: 'agent-conversations', label: t('menu.agentConversations'), icon: getMenuIcon('agent-conversations') },
            { key: 'agent-messages', label: t('menu.agentMessages'), icon: getMenuIcon('agent-messages') },
            { key: 'agent-workflow', label: t('menu.agentWorkflow') || '工作流管理', icon: getMenuIcon('agent-workflow') },
            {
              key: 'ibuild-proxy-group',
              label: t('menu.ibuildProxy'),
              icon: getMenuIcon('ibuild-proxy-group'),
              children: [
                { key: 'ibuild-proxy-agent', label: t('menu.ibuildProxyAgent'), icon: getMenuIcon('ibuild-proxy-agent') },
                { key: 'ibuild-proxy-data', label: t('menu.ibuildProxyData'), icon: getMenuIcon('ibuild-proxy-data') },
                { key: 'ibuild-proxy-mq', label: t('menu.ibuildProxyMQ'), icon: getMenuIcon('ibuild-proxy-mq') },
                { key: 'ibuild-proxy-cleanup', label: t('menu.ibuildProxyCleanup'), icon: getMenuIcon('ibuild-proxy-cleanup') },
              ],
            },
          ],
        },
        {
          key: 'rowmessage-group',
          label: t('menu.rawmessageGroup'),
          icon: getMenuIcon('rowmessage-group'),
          children: [
            { key: 'raw-messages', label: t('menu.rawMessages'), icon: getMenuIcon('raw-messages') },
          ],
        },
      ],
    },
    {
      key: 'organization-management',
      icon: getMenuIcon('organization-management'),
      label: t('menu.organizationManagement'),
      children: [
        { key: 'employee-management', icon: getMenuIcon('employee-management'), label: t('menu.employeeManagement') },
        { key: 'company-management', icon: getMenuIcon('company-management'), label: t('menu.companyManagement') },
      ],
    },
    { key: 'test-agent', icon: getMenuIcon('test-agent'), label: t('menu.testAgent') },
    { key: 'email-config', icon: getMenuIcon('email-config'), label: t('menu.emailConfig') },
  ] : [
    // 非超级管理员菜单：Job Management、Instructions、Modeling、Templates、Role Permissions
    {
      key: 'job',
      icon: getMenuIcon('job'),
      label: t('menu.jobs'),
      children: [
        { key: 'instruction-sync', icon: getMenuIcon('instruction-sync'), label: t('menu.instructions') },
        { key: 'modeling-sync', icon: getMenuIcon('modeling-sync'), label: t('menu.modeling') },
        { key: 'sql-pair-sync', icon: getMenuIcon('sql-pair-sync'), label: t('sqlPair.title') },
      ],
    },
    { key: 'instructions', icon: getMenuIcon('instructions'), label: t('menu.instructions') },
    { key: 'sql-pairs', icon: getMenuIcon('sql-pairs'), label: t('sqlPair.title') },
    { key: 'modeling', icon: getMenuIcon('modeling'), label: t('menu.modeling') },
    { key: 'template', icon: getMenuIcon('template'), label: t('menu.templates') },
    {
      key: 'role-permissions',
      icon: getMenuIcon('role-permissions'),
      label: t('menu.rolePermissions'),
      children: [
        { key: 'role-permissions-manager', icon: getMenuIcon('role-permissions-manager'), label: t('menu.rolePermissions') },
        { key: 'user-permissions-manager', icon: getMenuIcon('user-permissions-manager'), label: t('menu.userPermissions') },
        { key: 'permissions-manager', icon: getMenuIcon('permissions-manager'), label: t('menu.permissions') },
        { key: 'permission-switch-config', icon: getMenuIcon('permission-switch-config'), label: t('menu.permissionSwitchConfig') },
      ],
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    setSelectedKey(key);
  };

  const renderContent = () => {
    switch (selectedKey) {
      case 'tenant-management':
        if (!isSuperAdmin) {
          setSelectedKey('server');
          return <ServiceManager key={refreshKey} />;
        }
        return <TenantManagement key={refreshKey} />;
      case 'user':
        if (!isSuperAdmin) {
          // 非超级管理员不允许访问用户管理，回退到租户管理
          setSelectedKey('tenant-management');
          return <TenantManagement key={refreshKey} />;
        }
        return <TenantManager key={refreshKey} />;
      case 'server':
        return <ServiceManager key={refreshKey} />;
      case 'health-check-logs':
        return (
          <ErrorBoundary>
            <HealthCheckLogs key={refreshKey} />
          </ErrorBoundary>
        );
      case 'email-config':
        return (
          <ErrorBoundary>
            <EmailConfigManager key={refreshKey} />
          </ErrorBoundary>
        );
      case 'instruction-sync':
        return <JobManager key={refreshKey} mode="instructions" />; // 只显示Sync Instructions按钮
      case 'modeling-sync':
        return <JobManager key={refreshKey} mode="models" />; // 只显示Sync Models按钮
      case 'role-permissions-manager':
        return <RolePermissionsManager key={refreshKey} />;
      case 'user-permissions-manager':
        return (
          <ErrorBoundary>
            <UserPermissionsManager key={refreshKey} />
          </ErrorBoundary>
        );
      case 'permissions-manager':
        return <PermissionsManager key={refreshKey} />;
      case 'permission-switch-config':
        return (
          <ErrorBoundary>
            <PermissionSwitchConfig key={refreshKey} />
          </ErrorBoundary>
        );
      case 'instructions':
        return <InstructionManager key={refreshKey} />;
      case 'sql-pairs':
        return (
          <ErrorBoundary>
            <SqlPairManager key={refreshKey} />
          </ErrorBoundary>
        );
      case 'sql-pair-sync':
        return <JobManager key={refreshKey} mode="sql-pairs" />;
      case 'deployment-management':
        return (
          <ErrorBoundary>
            <DeploymentManagement key={refreshKey} />
          </ErrorBoundary>
        );
      case 'modeling':
        return <ModelingManager key={refreshKey} />;
      case 'sync-manager':
        if (!isSuperAdmin) {
          setSelectedKey('instructions');
          return <InstructionManager key={refreshKey} />;
        }
        return (
          <PermissionGate requireSuperAdmin>
            <ErrorBoundary>
              <SyncManager key={refreshKey} />
            </ErrorBoundary>
          </PermissionGate>
        );
      case 'template':
        return <TemplateManager key={refreshKey} />;
      case 'mcp':
        return <MCPManager key={refreshKey} />;
      case 'whatsapp-partners':
        return (
          <ErrorBoundary>
            <WhatsAppPartnersPage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'whatsapp-accounts':
        return (
          <ErrorBoundary>
            <WhatsAppAccountsPage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'agents':
        return (
          <ErrorBoundary>
            <AgentsManagement key={refreshKey} />
          </ErrorBoundary>
        );
      case 'account-mappings':
        return (
          <ErrorBoundary>
            <AccountMappingsPage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'connector-monitor':
        return (
          <ErrorBoundary>
            <ConnectorMonitoringPage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'whatsapp-test':
        return (
          <ErrorBoundary>
            <WhatsAppConnectorTestPage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'whatsapp-messages':
        return (
          <ErrorBoundary>
            <WhatsAppConnectorMonitorPage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'ibuild-proxy-data':
        return (
          <ErrorBoundary>
            <IBuildProxyDataViewer key={refreshKey} />
          </ErrorBoundary>
        );
      case 'ibuild-proxy-mq':
        return (
          <ErrorBoundary>
            <IBuildProxyMQManagement key={refreshKey} />
          </ErrorBoundary>
        );
      case 'ibuild-proxy-cleanup':
        return (
          <ErrorBoundary>
            <IBuildProxyCleanupManagement key={refreshKey} />
          </ErrorBoundary>
        );
      case 'ibuild-proxy-agent':
        return (
          <ErrorBoundary>
            <IBuildProxyAgentManagement key={refreshKey} />
          </ErrorBoundary>
        );
      case 'agent-conversations':
        return (
          <ErrorBoundary>
            <AgentConversationsPage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'agent-messages':
        return (
          <ErrorBoundary>
            <AgentMessagesPage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'agent-workflow':
        return (
          <ErrorBoundary>
            <AgentWrokflowManager key={refreshKey} />
          </ErrorBoundary>
        );
      case 'raw-messages':
        return (
          <ErrorBoundary>
            <RawMessagesPage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'user-profile':
        return (
          <ErrorBoundary>
            <UserProfilePage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'employee-management':
        return (
          <ErrorBoundary>
            <EmployeeManagementPage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'company-management':
        return (
          <ErrorBoundary>
            <CompanyManagementPage key={refreshKey} />
          </ErrorBoundary>
        );
      case 'test-agent':
        return (
          <ErrorBoundary>
            <TestAgentManager key={refreshKey} />
          </ErrorBoundary>
        );
      case 'settings':
        return <UserSettings key={refreshKey} />;
      case 'i18n-test':
        return <InternationalizationTest key={refreshKey} />;
      default:
        return <TenantManagement key={refreshKey} />;
    }
  };

  const getPageTitle = () => {
    switch (selectedKey) {
      case 'tenant-management':
        return t('menu.tenants');
      case 'user':
        return t('menu.user') + ' ' + t('common.management', { context: 'user' });
      case 'server':
        return t('menu.services');
      case 'health-check-logs':
        return '健康检查日志';
      case 'email-config':
        return '邮件配置';
      case 'instruction-sync':
        return t('menu.instructions') + ' ' + t('common.sync');
      case 'modeling-sync':
        return t('menu.modeling') + ' ' + t('common.sync');
      case 'role-permissions-manager':
        return t('menu.rolePermissions');
      case 'user-permissions-manager':
        return t('menu.userPermissions');
      case 'permissions-manager':
        return t('menu.rolePermissions');
      case 'permission-switch-config':
        return t('menu.permissionSwitchConfig');
      case 'instructions':
        return t('menu.instructions');
      case 'sql-pairs':
        return t('sqlPair.title');
      case 'sql-pair-sync':
        return t('sqlPair.title') + ' ' + t('common.sync');
      case 'deployment-management':
        return t('menu.deploymentManagement');
      case 'modeling':
        return t('menu.modeling');
      case 'sync-manager':
        return t('menu.syncManager');
      case 'template':
        return t('menu.templates');
      case 'mcp':
        return t('menu.mcp');
      case 'whatsapp-accounts':
        return t('whatsapp.accounts.title');
      case 'agents':
        return t('menu.agents');
      case 'account-mappings':
        return t('whatsapp.mappings.title');
      case 'connector-monitor':
        return t('menu.connectorMonitor');
      case 'connector-test':
        return t('menu.whatsappTest');
      case 'whatsapp-messages':
        return t('menu.whatsappMessages');
      case 'agent-workflow':
        return t('menu.agentWorkflow') || '工作流管理';
      case 'ibuild-proxy-agent':
        return t('menu.ibuildProxyAgent');
      case 'ibuild-proxy-data':
        return t('menu.ibuildProxyData');
      case 'ibuild-proxy-mq':
        return t('menu.ibuildProxyMQ');
      case 'ibuild-proxy-cleanup':
        return t('menu.ibuildProxyCleanup');
      case 'employee-management':
        return t('menu.employeeManagement');
      case 'company-management':
        return t('menu.companyManagement');
      case 'organization-management':
        return t('menu.organizationManagement');
      case 'test-agent':
        return t('menu.testAgent');
      case 'settings':
        return t('settings.title');
      case 'i18n-test':
        return t('menu.i18nTest');
      default:
        return t('app.title');
    }
  };

  const getPageIcon = () => getMenuIcon(selectedKey);

  const userMenuItems = [
    {
      key: 'profile',
      icon: getMenuIcon('user-profile'),
      label: t('menu.profile'),
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: t('menu.logout'),
    },
  ];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    if (key === 'profile') {
      setSelectedKey('user-profile');
      return;
    }
    if (key === 'logout') {
      logout().finally(() => {
        window.location.href = '/login';
      });
      return;
    }
  };

  const handleTenantChange = (tenantCode: string) => {
    console.log('租户已更改为:', tenantCode);
    // 触发当前页面数据的重新加载
    setRefreshKey(prev => prev + 1);
  };

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider 
        collapsible 
        collapsed={collapsed} 
        onCollapse={setCollapsed}
        width={300}
        collapsedWidth={64}
        theme="light"
        style={{
          boxShadow: '2px 0 8px rgba(0,0,0,0.1)',
        }}
      >
        <div style={{ 
          padding: '16px', 
          textAlign: 'center',
          borderBottom: '1px solid #f0f0f0'
        }}>
          <Title level={4} style={{ margin: 0, color: '#1890ff' }}>
            {collapsed ? t('app.brandShort') : t('app.brandFull')}
          </Title>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={handleMenuClick}
          style={{ borderRight: 0 }}
        />
      </Sider>
      
      <Layout>
        <Header style={{ 
          background: '#fff', 
          padding: '0 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <Title level={4} style={{ margin: 0 }}>
            <Space>
              {getPageIcon()}
              {getPageTitle()}
            </Space>
          </Title>
          
          <Space size="large">
            {/* 租户选择器 */}
            <TenantSelector onTenantChange={handleTenantChange} />
            
            {/* 初始化向导按钮 */}
            {/* 用户菜单 */}
            <Dropdown
              menu={{
                items: userMenuItems,
                onClick: handleUserMenuClick,
              }}
              placement="bottomRight"
            >
              <Button type="text" style={{ height: 'auto', padding: '4px 8px' }}>
                <Avatar size="small" icon={<UserOutlined />} />
                <span style={{ marginLeft: 8 }}>{user?.username || t('common.admin')}</span>
              </Button>
            </Dropdown>
          </Space>
        </Header>
        
        <Content style={{ 
          margin: '24px',
          padding: 0,
          background: '#f5f5f5',
          borderRadius: '8px',
          overflow: 'auto'
        }}>
          {renderContent()}
        </Content>
      </Layout>
      
    </Layout>
  );
};

export default MainLayout;
